/**
 * React hooky pro BMS — data fetching + selektory ze Zustand storu.
 */

import { useCallback, useEffect } from "react";
import { toast } from "sonner";
import { getBmsList, getBmsSnapshot } from "@client/api/bms";
import { useBmsStore } from "@client/store/bms";

/** Načte seznam BMS z REST API a uloží do storu. */
export const useFetchBmsList = () => {
  const setDevices = useBmsStore((s) => s.setDevices);
  return useCallback(async () => {
    try {
      const res = await getBmsList();
      setDevices(res.responseObject ?? []);
    } catch (err) {
      toast.error("Nepodařilo se načíst seznam BMS");
      console.error(err);
    }
  }, [setDevices]);
};

/** Načte snapshot daného BMS a aktualizuje store. */
export const useFetchBmsSnapshot = (slug: string | undefined) => {
  const applyState = useBmsStore((s) => s.applyState);
  return useCallback(async () => {
    if (!slug) return;
    try {
      const res = await getBmsSnapshot(slug);
      const runtime = res.responseObject?.runtime;
      if (runtime) {
        applyState({
          slug,
          status: runtime.status,
          deviceInfo: runtime.deviceInfo ?? undefined,
          settings: runtime.settings ?? undefined,
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, [slug, applyState]);
};

/** Auto-load + refresh BMS list jednou po mountu. */
export const useBmsList = () => {
  const devices = useBmsStore((s) => s.devices);
  const loaded = useBmsStore((s) => s.loaded);
  const fetch = useFetchBmsList();
  useEffect(() => {
    if (!loaded) void fetch();
  }, [loaded, fetch]);
  return { devices, loaded, refresh: fetch };
};

/** Hook pro jeden BMS — slévá DB record + runtime + live telemetrii. */
export const useBmsDevice = (slug: string | undefined) => {
  const device = useBmsStore((s) => s.devices.find((d) => d.slug === slug));
  const telemetry = useBmsStore((s) => (slug ? s.telemetry.get(slug) : undefined));
  const history = useBmsStore((s) => (slug ? s.cellHistory.get(slug) : undefined));
  return { device, telemetry, history };
};
